import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  MdClose,
  MdPhone,
  MdEmail,
  MdOutlineInfo,
  MdSecurity,
  MdBlock,
} from "react-icons/md";
import { FaWhatsapp } from "react-icons/fa";
import { TbShieldAlert, TbClockHour4, TbWallet } from "react-icons/tb";
import { useLockBodyScroll } from "../../Hooks/useLockBodyScroll";

const BuyerPenaltyInfoModal = ({ isOpen, onClose }) => {
  const [animate, setAnimate] = useState(false);
  useLockBodyScroll(isOpen);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => setAnimate(true), 10);
    } else {
      setAnimate(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    setAnimate(false);
    setTimeout(() => {
      onClose();
    }, 250);
  };

  if (!isOpen) return null;

  const handleCallSupport = () => {
    window.open("tel:+918339903307", "_self");
  };

  const handleEmailSupport = () => {
    const subject = encodeURIComponent("Buyer Penalty Payment & Account Inquiry");
    const body = encodeURIComponent(
      "Hello Support Team,\n\nI have inquiries regarding my buyer penalties and would like assistance with settlement.\n\nThank you."
    );
    window.open(
      `mailto:pcmexportsbbsr@gmail.com?subject=${subject}&body=${body}`,
      "_self"
    );
  };

  const handleWhatsAppSupport = () => {
    const message = encodeURIComponent(
      "Hello, I have inquiries regarding pending buyer penalties on PCM Exports. Please assist me. Thank you."
    );
    window.open(`https://wa.me/918339903307?text=${message}`, "_blank");
  };

  return createPortal(
    <div
      className={`fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/30 backdrop-blur-sm transition-opacity duration-300 ${
        animate ? "opacity-100" : "opacity-0"
      }`}
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div
        className={`bg-white dark:bg-gray-900 w-full md:w-[520px] md:rounded-3xl rounded-t-3xl shadow-2xl flex flex-col transition-all duration-300 overflow-hidden ${
          animate
            ? "scale-100 translate-y-0"
            : "scale-95 md:translate-y-4 translate-y-full"
        }`}
        style={{ maxHeight: "90vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 bg-primaryColor text-white flex items-center justify-between flex-shrink-0">
          <button
            onClick={handleClose}
            className="text-2xl leading-none text-white/90 hover:text-white transition cursor-pointer"
          >
            ←
          </button>
          <div className="flex-1 text-center font-semibold text-lg text-white">
            About Buyer Penalties
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-white/80 hover:text-white hover:bg-white/15 transition cursor-pointer"
          >
            <MdClose size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 sm:p-6 space-y-4 overflow-y-auto flex-1 text-xs leading-relaxed text-gray-600 dark:text-gray-300">
          {/* Section 1: Overview */}
          <div className="p-3.5 rounded-xl bg-gray-50 dark:bg-gray-800/60 border border-gray-100 dark:border-gray-700/60 space-y-1.5">
            <h4 className="font-bold text-gray-900 dark:text-white text-xs flex items-center gap-1.5">
              <TbClockHour4 className="text-primaryColor" size={16} />
              What are Buyer Penalties?
            </h4>
            <p>
              Buyer penalties and dues are issued in cases such as Order Fulfillment Mandate (OFM) allowances, order defaults, or post-confirmation cancellations. These charges help compensate logistics and verified suppliers for reserved resources.
            </p>
          </div>

          {/* Section 2: Wallet Settlement */}
          <div className="p-3.5 rounded-xl bg-blue-50/60 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/40 space-y-1.5 text-blue-950 dark:text-blue-200">
            <h4 className="font-bold text-xs flex items-center gap-1.5 text-blue-800 dark:text-blue-300">
              <TbWallet size={16} />
              Instant Settlement via Wallet
            </h4>
            <p>
              You can instantly clear pending penalties using your available wallet balance. If your balance is low, you can quickly recharge your wallet using UPI, Cards, or Net Banking, and settle with 1-click.
            </p>
          </div>

          {/* Section 3: Blocking Policy */}
          <div className="p-3.5 rounded-xl bg-red-50/60 dark:bg-red-900/20 border border-red-100 dark:border-red-800/40 space-y-1.5 text-red-950 dark:text-red-200">
            <h4 className="font-bold text-xs flex items-center gap-1.5 text-red-800 dark:text-red-300">
              <MdBlock size={16} />
              Account & Order Restrictions
            </h4>
            <p>
              Unsettled dues beyond the grace period will automatically pause your ability to post new requirements and confirm orders. Once all pending dues are cleared, your account privileges are unblocked automatically in real-time.
            </p>
          </div>

          {/* Section 4: Contact & Grievances */}
          <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-800/60 border border-gray-100 dark:border-gray-700/60 space-y-3">
            <h4 className="font-bold text-gray-900 dark:text-white text-xs flex items-center gap-1.5">
              <MdSecurity className="text-emerald-600" size={16} />
              Need Help or Dispute a Penalty?
            </h4>
            <p className="text-gray-500 dark:text-gray-400">
              If you believe a penalty was applied incorrectly, please reach out to our dedicated support team with your Penalty ID and Order Number.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-1">
              <button
                type="button"
                onClick={handleCallSupport}
                className="flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-primaryColor hover:text-primaryColor transition font-semibold text-gray-700 dark:text-gray-200 cursor-pointer"
              >
                <MdPhone size={14} className="text-primaryColor" />
                <span>Call Us</span>
              </button>
              <button
                type="button"
                onClick={handleWhatsAppSupport}
                className="flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-emerald-500 hover:text-emerald-600 transition font-semibold text-gray-700 dark:text-gray-200 cursor-pointer"
              >
                <FaWhatsapp size={14} className="text-emerald-500" />
                <span>WhatsApp</span>
              </button>
              <button
                type="button"
                onClick={handleEmailSupport}
                className="flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-primaryColor hover:text-primaryColor transition font-semibold text-gray-700 dark:text-gray-200 cursor-pointer"
              >
                <MdEmail size={14} className="text-primaryColor" />
                <span>Email</span>
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3.5 bg-gray-50 dark:bg-gray-800/40 border-t border-gray-100 dark:border-gray-800 flex justify-end flex-shrink-0">
          <button
            type="button"
            onClick={handleClose}
            className="px-5 py-2 rounded-xl bg-primaryColor text-white text-xs font-bold hover:bg-primaryColor/90 transition shadow-xs cursor-pointer"
          >
            Got It
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default BuyerPenaltyInfoModal;
